import { SolFieldDescriptor } from '@sol/components';

export const AsmSettingContext = {
  global: 'global',
  partner: 'partner',
  scope: 'scope',
  subscope: 'subscope',
  user: 'user',
} as const;

export type AsmSettingContextId = (typeof AsmSettingContext)[keyof typeof AsmSettingContext];

export const AsmSettingDefinitionId = {
  uiPreferences: 'ui_preferences',
  tableLayout: 'table_layout',
} as const;

/**
 * A settings payload value. `null` is carried rather than mapped to `undefined` because on this wire it
 * is a value, not an absence: it clears the field. That is why it survives past the DTO boundary here.
 */
export type AsmSettingValue = string | number | boolean | string[] | AsmTableColumnWidths | null;

/** Pixel widths keyed by column id; a column without a key sizes itself. */
export type AsmTableColumnWidths = Record<string, number>;

/** The data a settings form holds: one value per payload column the definition declares. */
export type AsmSettingFormData = Record<string, AsmSettingValue | undefined>;

export interface AsmSettingDefinitionContext {
  context: AsmSettingContextId;
  managePermission: string;
  iMayWrite: boolean;
}

export interface AsmSettingDefinition {
  id: string;
  displayName: string;
  description: string;
  entryKey?: string;
  contexts: AsmSettingDefinitionContext[];
  fields: SolFieldDescriptor[];
}

export type AsmSettingPayloadRow = Record<string, AsmSettingValue>;

export interface AsmSettingEntriesPayload {
  entries: AsmSettingPayloadRow[];
}

export type AsmSettingPayload = AsmSettingPayloadRow | AsmSettingEntriesPayload;

export interface AsmSettingRule {
  id: number;
  definition: string;
  context: AsmSettingContextId;
  target?: string;
  targetLabel: string;
  payload: AsmSettingPayload;
  iMayWrite: boolean;
  updatedBy?: string;
  updatedAt?: Date;
}

export interface AsmSettingRuleDraft {
  definition: string;
  context: AsmSettingContextId;
  targets: string[];
  payload: AsmSettingPayload;
}

/**
 * One thing a rule can be written to. `label` is what it is called — a scope's display name, a
 * subscope's own name, a user's email — and `hint` is the second line that tells two of them apart:
 * the raw id beside a scope, the parent scope beside a subscope, and nothing at all for a partner or
 * a user, whose label is already unique.
 */
export interface AsmSettingTarget {
  id?: string;
  label: string;
  hint?: string;
  alreadySet: boolean;
}

export const AsmSettingRefusalReason = {
  notSelfService: 'not_self_service',
  noPermission: 'no_permission',
} as const;

export type AsmSettingRefusalReasonId = (typeof AsmSettingRefusalReason)[keyof typeof AsmSettingRefusalReason];

export interface AsmSettingRefusal {
  reason: AsmSettingRefusalReasonId;
  context?: AsmSettingContextId;
}

export interface AsmResolvedValue {
  value: AsmSettingValue;
  context?: AsmSettingContextId;
  editable: boolean;
  refusal?: AsmSettingRefusal;
}

export interface AsmResolvedEntry {
  values: AsmSettingPayloadRow;
  context?: AsmSettingContextId;
  editable: boolean;
  refusal?: AsmSettingRefusal;
}

export type AsmResolvedLayout = Omit<AsmResolvedEntry, 'editable' | 'refusal'>;

export interface AsmResolvedDefinition {
  fields: Record<string, AsmResolvedValue>;
  entries: AsmResolvedEntry[];
}

export type AsmResolvedSettings = Record<string, AsmResolvedDefinition>;

export interface AsmSettingRefusedTarget {
  target: string;
  reason: string;
}
